import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { type SupabaseClient, createClient } from '@supabase/supabase-js';
import { SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from '../config';

/**
 * Storage adapter backed by `@capacitor/preferences`.
 *
 * The native shell needs it: the PKCE code verifier must survive the WKWebView
 * navigating away to the identity provider and back, and Preferences is real
 * native storage rather than WebView-scoped. supabase-js accepts async storage,
 * so no wrapper gymnastics are needed.
 */
const capacitorStorage = {
  getItem: async (key: string): Promise<string | null> => {
    const { value } = await Preferences.get({ key });
    return value ?? null;
  },
  setItem: async (key: string, value: string): Promise<void> => {
    await Preferences.set({ key, value });
  },
  removeItem: async (key: string): Promise<void> => {
    await Preferences.remove({ key });
  },
};

/** False when the build was made without Supabase credentials. */
export const isAuthConfigured = Boolean(SUPABASE_URL && SUPABASE_PUBLISHABLE_KEY);

export const isNative = Capacitor.isNativePlatform();

/**
 * Created even when unconfigured — with placeholder values so importing this
 * module never throws. `isAuthConfigured` is what the session bootstrap branches
 * on, so a build without credentials fails with a sentence that names the two
 * variables rather than a 401 from a placeholder host.
 */
export const supabase: SupabaseClient = createClient(
  SUPABASE_URL || 'http://localhost:54321',
  SUPABASE_PUBLISHABLE_KEY || 'unconfigured',
  {
    auth: {
      flowType: 'pkce',
      persistSession: true,
      autoRefreshToken: true,
      ...(isNative
        ? {
            storage: capacitorStorage,
            // Native returns through a deep link, not a page load: App.tsx hands
            // the code to `exchangeCodeForSession` itself. Letting supabase-js
            // also scan the URL would race that exchange.
            detectSessionInUrl: false,
          }
        : {
            // On the web the OAuth redirect *is* a page load, so let supabase-js
            // pick the session out of the URL.
            detectSessionInUrl: true,
          }),
    },
  },
);
